import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'
import { message, ui } from '../canvass/model.ts'

/**
 * Walker scripts (the old system's "Walker Scripts" / "Voice Script";
 * decisions.md 2026-10-25, rows W10 and A13). What walkers read at the door;
 * a canvass picks one or more and walkers see them as tabs. A script a canvass
 * uses cannot be deleted, as before.
 */

interface Script {
  script_id: string
  title: string
  body: string
  updated_at: string
  canvasses: number
}

export function Scripts() {
  const client = useQueryClient()
  const { campaignId, campaign } = useSelectedCampaign()
  const allowed = campaign?.can_manage_canvassers === true
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ['walker-scripts', campaignId],
    queryFn: () => apiGet<{ scripts: Script[] }>(`/campaigns/${campaignId}/walker-scripts`),
    enabled: Boolean(campaignId) && allowed,
  })
  const refresh = () => client.invalidateQueries({ queryKey: ['walker-scripts', campaignId] })

  const save = useMutation({
    mutationFn: () =>
      editing === 'new'
        ? apiPost(`/campaigns/${campaignId}/walker-scripts`, { title: title.trim(), body })
        : apiPut(`/campaigns/${campaignId}/walker-scripts/${editing}`, { title: title.trim(), body }),
    onSuccess: async () => {
      setError(null)
      setEditing(null)
      await refresh()
    },
    onError: (caught) => setError(message(caught)),
  })
  const remove = useMutation({
    mutationFn: (id: string) => apiPost(`/campaigns/${campaignId}/walker-scripts/${id}/delete`),
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: (caught) => setError(message(caught)),
  })

  const start = (s?: Script) => {
    setEditing(s ? s.script_id : 'new')
    setTitle(s?.title ?? '')
    setBody(s?.body ?? '')
  }

  const form = (
    <form
      className="space-y-2 rounded-lg border border-line bg-surface-sunken p-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (title.trim()) save.mutate()
      }}
    >
      <input id="sc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" maxLength={200}
        aria-label="Title" className={`${ui.input} w-full`} />
      <textarea id="sc-body" value={body} onChange={(e) => setBody(e.target.value)} rows={8} maxLength={20000}
        aria-label="Script" placeholder="Hi, my name is … I'm a volunteer with …" className={`${ui.input} w-full`} />
      <div className="flex gap-2">
        <button type="submit" className={ui.primary} disabled={!title.trim() || save.isPending}>Save</button>
        <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setEditing(null)}>Cancel</button>
      </div>
    </form>
  )

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Scripts</h1>
          <p className="mt-1 text-sm text-ink-muted">What walkers read at the door. Pick them for a canvass in its first step.</p>
        </div>
        {allowed && editing !== 'new' ? (
          <button type="button" className={ui.primary} onClick={() => start()}>New script</button>
        ) : null}
      </div>
      {campaign && !allowed ? (
        <p className="mt-6 text-sm text-ink-muted">Only the Campaign Manager can manage scripts on this campaign.</p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-4 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm">{error}</p>
      ) : null}
      {editing === 'new' ? <div className="mt-4">{form}</div> : null}
      {allowed ? (
        <ul className="mt-4 space-y-3">
          {(list.data?.scripts ?? []).map((s) =>
            editing === s.script_id ? (
              <li key={s.script_id}>{form}</li>
            ) : (
              <li key={s.script_id} className="rounded-lg border border-line p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="font-semibold">{s.title}</h2>
                  <span className="text-xs text-ink-muted">
                    {s.canvasses > 0 ? `used by ${s.canvasses} canvass${s.canvasses === 1 ? '' : 'es'}` : 'not used'}
                  </span>
                </div>
                <p className="mt-1 max-w-prose whitespace-pre-wrap text-sm">{s.body}</p>
                <div className="mt-2 flex gap-2">
                  <button type="button" className={ui.secondary} onClick={() => start(s)}>Edit</button>
                  <button type="button" className={ui.secondary} disabled={s.canvasses > 0 || remove.isPending}
                    title={s.canvasses > 0 ? 'Take it off its canvasses first' : undefined}
                    onClick={() => remove.mutate(s.script_id)}>
                    Delete
                  </button>
                </div>
              </li>
            ),
          )}
          {list.data && list.data.scripts.length === 0 && editing !== 'new' ? (
            <li className="text-sm text-ink-muted">No scripts yet.</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  )
}
